import axios from 'axios'
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { store } from '@Redux/store'
import { readCsrfToken, setCsrfToken } from '@Api/csrfToken'

let state: typeof store
export const injectStore = (_state: any) => {
	state = _state
}

/**
 * The session credential is an httpOnly cookie set by the API. It is not
 * readable here and must never be: that is what stops an XSS from stealing it,
 * which localStorage could not do.
 *
 * `withCredentials` / `credentials: 'include'` is therefore load-bearing — the
 * browser will not attach the cookie on a cross-origin request without it, and
 * the portal and API are on different hosts in every environment.
 */
const api = axios.create({
	baseURL: process.env.REACT_APP_API_BASE_URL,
	withCredentials: true,
})

const CSRF_HEADER = 'x-csrf-token'

/**
 * Make sure a CSRF token exists before a mutating request goes out.
 *
 * The token lives in memory, so a page reload starts with none and it is only
 * repopulated when `bootstrapAuthAction` finishes. A student who refreshed and
 * immediately submitted — an exam answer, a profile save — raced that boot and
 * got a 403 on a perfectly good connection, which on the exam path surfaces as
 * "answers could not be saved".
 *
 * One in-flight fetch, shared by every caller that arrives during the gap. On
 * failure it resolves anyway and the request proceeds without a token: the
 * server will reject it, which is the same outcome as not trying, and blocking
 * forever would be worse.
 */
let csrfFetch: Promise<void> | null = null

/**
 * When the last /auth/me came back without a usable token.
 *
 * Without a cooldown, a backend that answers but never returns a token leaves
 * the `readCsrfToken()` guard permanently unsatisfied, so EVERY subsequent
 * request fires another /auth/me — no backoff, no cap. Request volume roughly
 * doubles and each request gains a serialized round trip, indefinitely.
 */
let csrfFailedAt = 0
const CSRF_RETRY_COOLDOWN_MS = 10_000

/**
 * Cap on how long a request will wait for the boot /auth/me before proceeding
 * without a token, and on the lazy fetch itself. A stalled /auth/me must not
 * wedge the request queue: proceeding tokenless is the long-standing fail-open
 * (the server rejects, which is the same outcome as not trying), while waiting
 * forever left a login or an exam save spinning with no error at all.
 */
const AUTH_BOOT_WAIT_CAP_MS = 8_000
const CSRF_FETCH_TIMEOUT_MS = 8_000

/** GET/HEAD/OPTIONS change nothing, so they need no token and never wait for one. */
export const SAFE_METHODS = ['get', 'head', 'options']

let authResolutionWait: Promise<void> | null = null

/**
 * Resolves once boot has answered "who is this" — bootstrapAuthAction on a
 * normal load, the impersonation redeem inside the admin embed; every terminal
 * path of both sets `authReady` — or after the cap, whichever comes first.
 */
const awaitAuthResolution = (): Promise<void> => {
	if (state?.getState().auth?.authReady) return Promise.resolve()
	if (!authResolutionWait) {
		authResolutionWait = new Promise<void>(resolve => {
			const done = () => {
				clearTimeout(cap)
				unsubscribe()
				authResolutionWait = null
				resolve()
			}
			const cap = setTimeout(done, AUTH_BOOT_WAIT_CAP_MS)
			const unsubscribe = state.subscribe(() => {
				if (state.getState().auth?.authReady) done()
			})
		})
	}
	return authResolutionWait
}

export const ensureCsrfToken = async (): Promise<void> => {
	if (readCsrfToken()) return

	// Bail only when the caller is KNOWN to be anonymous.
	//
	// This used to test `isAuth` alone, which is false for the whole /auth/me
	// round trip — so it returned instantly during exactly the boot race the
	// comment above describes, and the mutation went out with no token and 403'd.
	// "Anonymous" and "haven't asked yet" are different states; `authReady` is
	// the flag that tells them apart, and only the first may skip the fetch.
	//
	// A known-anonymous caller still returns immediately, so login, signup and
	// forgot-password keep costing no extra round trip: they are submitted long
	// after boot, by which time authReady is true.
	let auth = state?.getState().auth
	if (auth?.authReady && !auth?.isAuth) return
	// No store injected yet (Storybook, tests). Nothing to protect, so do not
	// block the request on a fetch that can only 401.
	if (!auth) return

	if (!auth.authReady) {
		// Boot window: an /auth/me is already in flight and its response is what
		// populates the token (Authentication.slice calls setCsrfToken on every
		// path). Firing our own /auth/me here doubled the identity call on every
		// cold load and, for a guest, serialized the first wave of requests
		// behind a round trip that could only 401. Wait for the in-flight answer
		// instead, capped so a stalled boot degrades to the fail-open below.
		await awaitAuthResolution()
		if (readCsrfToken()) return
		auth = state?.getState().auth
		// Resolved anonymous — no token needed — or the cap elapsed unresolved,
		// where sending tokenless and letting the server decide beats hanging.
		if (!auth?.authReady || !auth?.isAuth) return
		// Resolved signed-in yet no token in the response: fall through to the
		// same lazy salvage the post-boot missing-token case uses.
	}

	if (Date.now() - csrfFailedAt < CSRF_RETRY_COOLDOWN_MS) return

	if (!csrfFetch) {
		csrfFetch = api
			.get('/auth/me', { timeout: CSRF_FETCH_TIMEOUT_MS })
			.then(({ data }) => {
				const token = data?.data?.csrfToken
				setCsrfToken(token)
				csrfFailedAt = token ? 0 : Date.now()
			})
			.catch(() => {
				csrfFailedAt = Date.now()
			})
			.finally(() => {
				csrfFetch = null
			})
	}
	await csrfFetch
}

/**
 * Method-aware ensure for the RTK baseQuery wrappers — the request args carry
 * the method there, which `prepareHeaders` never sees. Several endpoints are
 * POSTs declared as `builder.query`, so the query/mutation type is no proxy.
 */
export const ensureCsrfTokenForArgs = async (args: string | { method?: string }): Promise<void> => {
	const method = (typeof args === 'string' ? 'get' : args.method ?? 'get').toLowerCase()
	if (!SAFE_METHODS.includes(method)) await ensureCsrfToken()
}

/**
 * Headers every request needs, in one place.
 *
 * There used to be three separate places attaching credentials — this callback,
 * an axios interceptor below, and a duplicate interceptor registered inside a
 * useEffect in ApiProvider. Three copies of auth logic is three chances to
 * disagree; the token is gone now and what remains lives here.
 */
const commonHeaders = (isEmbed: boolean): Record<string, string> => {
	const headers: Record<string, string> = {
		// Falls back to Arabic, not English: 'lang' is only empty before i18n has
		// run once, and the platform default is Arabic (DEFAULT_LANGUAGE in
		// src/Locale). Defaulting to en-GB here made a first request on a deep link
		// come back in English while the UI around it rendered in Arabic.
		'accept-language': localStorage.getItem('lang') ?? 'ar-EG',
	}
	// No DeviceId header. It carried a client-computed fingerprint that the
	// server used to key device identity; identity is server-minted and rides
	// the device cookie now, so sending it on every request would be a
	// client-supplied identifier that nothing reads — and that a future reader
	// could easily mistake for something meaningful. The fingerprint itself is
	// still sent, once, in the login body, purely as a re-adoption signal.
	if (isEmbed) headers['x-embed'] = 'admin'
	const csrf = readCsrfToken()
	if (csrf) headers[CSRF_HEADER] = csrf
	return headers
}

/**
 * Shared header builder for both RTK roots.
 *
 * The CSRF ensure used to live in here, but `prepareHeaders` cannot see the
 * request method, so plain GETs waited on the token fetch too — on a cold load
 * that serialized the whole first wave of queries, guest landing page
 * included, behind an /auth/me they never needed. Each root's baseQuery
 * wrapper now calls `ensureCsrfTokenForArgs` with the request args, where the
 * method IS visible, before the request builds its headers here.
 */
export const rtkPrepareHeadersCallback = async (
	headers: Headers,
	{ getState }: Pick<BaseQueryApi, 'getState' | 'type' | 'extra' | 'endpoint' | 'forced'>
) => {
	const isEmbed = (getState?.() as any)?.utils?.isEmbed as boolean
	Object.entries(commonHeaders(isEmbed)).forEach(([key, value]) => headers.set(key, value))
	return headers
}

api.interceptors.request.use(
	async function (config) {
		try {
			// Only mutations need the token, and only they are worth delaying for.
			// `/auth/me` is excluded explicitly or the lazy fetch would recurse.
			const method = (config.method ?? 'get').toLowerCase()
			if (!SAFE_METHODS.includes(method) && !config.url?.endsWith('/auth/me')) {
				await ensureCsrfToken()
			}

			const isEmbed = state?.getState().utils.isEmbed ?? false
			// Merge rather than replace: the previous version overwrote
			// config.headers wholesale, silently dropping any per-call header.
			config.headers = { ...(config.headers ?? {}), ...commonHeaders(isEmbed) }
			return config
		} catch (error) {
			return Promise.reject(error)
		}
	},
	function (error) {
		return Promise.reject(error.response)
	}
)

export default api
